/**
 * Evidence Collector Service
 *
 * Continuously captures debugging evidence in ring buffers:
 *  - Console logs (log/warn/error)
 *  - Network requests/responses (via fetch interceptor)
 *  - Redux state snapshots
 *  - User actions (dispatched Redux actions)
 *
 * When an issue report is submitted, the collected evidence is bundled
 * and PHI-masked before being sent to the backend.
 */
import { store } from '../store';
import { maskReduxState, maskNetworkBody, maskString, maskValue } from '../utils/phiMasking';

// ── Types ───────────────────────────────────────────────────────────────

export interface ConsoleEntry {
  timestamp: string;
  level: 'INFO' | 'WARNING' | 'ERROR';
  message: string;
}

export interface NetworkEntry {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  duration: number;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
}

export interface ReduxActionEntry {
  timestamp: string;
  type: string;
  payloadPreview?: unknown;
}

export interface ReplayEvent {
  timestamp: string;
  type: 'click' | 'input' | 'scroll' | 'focus' | 'blur' | 'keydown';
  target: string;
  value?: string;
  scrollX?: number;
  scrollY?: number;
  key?: string;
}

export interface EvidenceBundle {
  console: ConsoleEntry[];
  network: NetworkEntry[];
  reduxState: unknown;
  reduxActions: ReduxActionEntry[];
  replayEvents: ReplayEvent[];
  browserInfo: {
    userAgent: string;
    url: string;
    timestamp: string;
    viewport: { width: number; height: number };
    language: string;
  };
}

// ── Ring buffers ────────────────────────────────────────────────────────

const MAX_CONSOLE = 50;
const MAX_NETWORK = 20;
const MAX_ACTIONS = 30;
const MAX_REPLAY = 100;

const _consoleBuffer: ConsoleEntry[] = [];
const _networkBuffer: NetworkEntry[] = [];
const _actionBuffer: ReduxActionEntry[] = [];
const _replayBuffer: ReplayEvent[] = [];

let _installed = false;
let _origDispatch: typeof store.dispatch | null = null;
let _replayHandlers: Array<{ event: string; handler: EventListener; capture: boolean }> = [];

// ── Console interception ────────────────────────────────────────────────

let _origLog = console.log;
let _origWarn = console.warn;
let _origError = console.error;

function _captureConsole(level: 'INFO' | 'WARNING' | 'ERROR', args: unknown[]) {
  const message = args
    .map((a) => {
      if (typeof a === 'string') return a;
      // Mask objects via key-based masking before serializing to catch PHI in JSON keys
      try {
        const masked = maskValue(a);
        return JSON.stringify(masked);
      } catch {
        return String(a);
      }
    })
    .join(' ');
  _consoleBuffer.push({
    timestamp: new Date().toISOString(),
    level,
    message: maskString(message),
  });
  if (_consoleBuffer.length > MAX_CONSOLE) _consoleBuffer.shift();
}

// ── Network interception ────────────────────────────────────────────────

let _origFetch: typeof window.fetch | null = null;
let _origXhrOpen: ((this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) => void) | null = null;
let _origXhrSend: ((this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) => void) | null = null;

function _pushNetworkEntry(entry: NetworkEntry) {
  _networkBuffer.push(entry);
  if (_networkBuffer.length > MAX_NETWORK) _networkBuffer.shift();
}

function _installFetchInterceptor() {
  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = (init?.method || 'GET').toUpperCase();
    const startTime = performance.now();

    // Capture request body (for non-GET requests)
    let reqBody: unknown = undefined;
    if (init?.body) {
      try {
        if (typeof init.body === 'string') {
          reqBody = maskNetworkBody(init.body);
        } else if (init.body instanceof FormData) {
          reqBody = '[FormData]';
        }
      } catch {
        reqBody = '[unparseable]';
      }
    }

    try {
      const response = await _origFetch!.call(window, input, init);
      const duration = Math.round(performance.now() - startTime);

      _pushNetworkEntry({
        timestamp: new Date().toISOString(),
        method,
        url: maskString(url),
        status: response.status,
        duration,
        requestBody: reqBody,
      });

      return response;
    } catch (err) {
      const duration = Math.round(performance.now() - startTime);
      _pushNetworkEntry({
        timestamp: new Date().toISOString(),
        method,
        url: maskString(url),
        status: 0,
        duration,
        requestBody: reqBody,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };
}

function _installXhrInterceptor() {
  // Axios uses XMLHttpRequest in browsers, so we must patch XHR too
  XMLHttpRequest.prototype.open = function patchedOpen(
    this: XMLHttpRequest,
    method: string,
    url: string,
    ...rest: unknown[]
  ) {
    // Store metadata on the XHR instance for use in send()
    (this as unknown as { _evidenceMethod: string })._evidenceMethod = method.toUpperCase();
    (this as unknown as { _evidenceUrl: string })._evidenceUrl = url;
    _origXhrOpen?.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function patchedSend(
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const xhr = this as unknown as XMLHttpRequest & {
      _evidenceMethod: string;
      _evidenceUrl: string;
      _evidenceStartTime: number;
      _evidenceReqBody: unknown;
    };
    xhr._evidenceStartTime = performance.now();

    // Capture request body
    let reqBody: unknown = undefined;
    if (body) {
      try {
        if (typeof body === 'string') {
          reqBody = maskNetworkBody(body);
        } else if (body instanceof FormData) {
          reqBody = '[FormData]';
        }
      } catch {
        reqBody = '[unparseable]';
      }
    }
    xhr._evidenceReqBody = reqBody;

    const onLoadEnd = () => {
      const duration = Math.round(performance.now() - xhr._evidenceStartTime);
      _pushNetworkEntry({
        timestamp: new Date().toISOString(),
        method: xhr._evidenceMethod,
        url: maskString(xhr._evidenceUrl),
        status: xhr.status ?? 0,
        duration,
        requestBody: xhr._evidenceReqBody,
        error: xhr.status === 0 ? 'Network error or aborted' : undefined,
      });
    };

    this.addEventListener('loadend', onLoadEnd);
    return _origXhrSend!.call(this, body);
  };
}

function _installNetworkInterceptor() {
  _installFetchInterceptor();
  _installXhrInterceptor();
}

// ── Redux action interception ───────────────────────────────────────────

function _installReduxInterceptor() {
  const origDispatch = store.dispatch.bind(store);
  _origDispatch = origDispatch;
  const patchedDispatch = function patchedDispatch(action: Parameters<typeof origDispatch>[0]) {
    if (action && typeof action === 'object' && 'type' in action) {
      const a = action as { type: string; payload?: unknown };
      let payloadPreview: unknown = undefined;
      if (a.payload !== undefined) {
        try {
          // Mask payload via key-based masking before serializing to catch PHI in keys
          const maskedPayload = maskValue(a.payload);
          const serialized = JSON.stringify(maskedPayload);
          payloadPreview = serialized.length > 500
            ? serialized.slice(0, 500) + '...[truncated]'
            : serialized;
        } catch {
          payloadPreview = '[unserializable]';
        }
      }
      _actionBuffer.push({
        timestamp: new Date().toISOString(),
        type: a.type,
        payloadPreview: payloadPreview,
      });
      if (_actionBuffer.length > MAX_ACTIONS) _actionBuffer.shift();
    }
    return origDispatch(action);
  } as typeof store.dispatch;

  store.dispatch = patchedDispatch;
}

// ── DOM event recording (session replay) ────────────────────────────────

function _describeTarget(el: EventTarget | null): string {
  if (!(el instanceof Element)) return 'unknown';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
    : '';
  const text = el.textContent ? el.textContent.trim().slice(0, 30) : '';
  const label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
  const desc = label || text ? `[${label || text}]` : '';
  return `${tag}${id}${cls}${desc}`;
}

function _pushReplayEvent(event: ReplayEvent) {
  _replayBuffer.push(event);
  if (_replayBuffer.length > MAX_REPLAY) _replayBuffer.shift();
}

function _installReplayRecorder() {
  // Throttle scroll events
  let lastScrollTs = 0;
  const SCROLL_THROTTLE_MS = 500;

  const handlers: Array<{ event: string; handler: EventListener; capture: boolean }> = [];

  const clickHandler: EventListener = (e) => {
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'click',
      target: _describeTarget(e.target),
    });
  };
  document.addEventListener('click', clickHandler, true);
  handlers.push({ event: 'click', handler: clickHandler, capture: true });

  const inputHandler: EventListener = (e) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target) return;
    // Mask input values for PHI protection
    const rawValue = target.value || '';
    const maskedValue = target instanceof HTMLInputElement && target.type === 'password'
      ? '[REDACTED]' : maskString(rawValue).slice(0, 100);
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'input',
      target: _describeTarget(e.target),
      value: maskedValue,
    });
  };
  document.addEventListener('input', inputHandler, true);
  handlers.push({ event: 'input', handler: inputHandler, capture: true });

  const scrollHandler: EventListener = () => {
    const now = Date.now();
    if (now - lastScrollTs < SCROLL_THROTTLE_MS) return;
    lastScrollTs = now;
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'scroll',
      target: 'window',
      scrollX: Math.round(window.scrollX),
      scrollY: Math.round(window.scrollY),
    });
  };
  document.addEventListener('scroll', scrollHandler, true);
  handlers.push({ event: 'scroll', handler: scrollHandler, capture: true });

  const focusHandler: EventListener = (e) => {
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'focus',
      target: _describeTarget(e.target),
    });
  };
  document.addEventListener('focus', focusHandler, true);
  handlers.push({ event: 'focus', handler: focusHandler, capture: true });

  const blurHandler: EventListener = (e) => {
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'blur',
      target: _describeTarget(e.target),
    });
  };
  document.addEventListener('blur', blurHandler, true);
  handlers.push({ event: 'blur', handler: blurHandler, capture: true });

  const keydownHandler: EventListener = (e) => {
    // Only record non-character keys to avoid capturing PHI via keystrokes
    const navKeys = ['Enter', 'Tab', 'Escape', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!(e as KeyboardEvent).key || !navKeys.includes((e as KeyboardEvent).key)) return;
    _pushReplayEvent({
      timestamp: new Date().toISOString(),
      type: 'keydown',
      target: _describeTarget(e.target),
      key: (e as KeyboardEvent).key,
    });
  };
  document.addEventListener('keydown', keydownHandler, true);
  handlers.push({ event: 'keydown', handler: keydownHandler, capture: true });

  _replayHandlers = handlers;
}

// ── Public API ──────────────────────────────────────────────────────────

/** Install all interceptors. Call once at app startup. */
function install(): void {
  if (_installed) return;
  _installed = true;

  // Capture current console methods — these may already be patched by
  // logCaptureService, so we chain to the patched versions to avoid
  // bypassing the log capture buffer.
  _origLog = console.log;
  _origWarn = console.warn;
  _origError = console.error;

  // Console
  console.log = (...args: unknown[]) => { _captureConsole('INFO', args); _origLog.apply(console, args); };
  console.warn = (...args: unknown[]) => { _captureConsole('WARNING', args); _origWarn.apply(console, args); };
  console.error = (...args: unknown[]) => { _captureConsole('ERROR', args); _origError.apply(console, args); };

  // Network — capture originals here, not at module import time
  _origFetch = window.fetch;
  _origXhrOpen = XMLHttpRequest.prototype.open;
  _origXhrSend = XMLHttpRequest.prototype.send;
  _installNetworkInterceptor();

  // Redux actions
  _installReduxInterceptor();

  // DOM event recording (session replay)
  _installReplayRecorder();
}

/** Uninstall all interceptors and restore original functions. */
function uninstall(): void {
  if (!_installed) return;
  _installed = false;

  // Restore console
  console.log = _origLog;
  console.warn = _origWarn;
  console.error = _origError;

  // Restore network
  if (_origFetch) window.fetch = _origFetch;
  if (_origXhrOpen) XMLHttpRequest.prototype.open = _origXhrOpen;
  if (_origXhrSend) XMLHttpRequest.prototype.send = _origXhrSend;
  _origFetch = null;
  _origXhrOpen = null;
  _origXhrSend = null;

  // Restore Redux dispatch
  if (_origDispatch) {
    store.dispatch = _origDispatch;
    _origDispatch = null;
  }

  // Remove DOM event listeners
  for (const { event, handler, capture } of _replayHandlers) {
    document.removeEventListener(event, handler, capture);
  }
  _replayHandlers = [];

  // Clear buffers
  clear();
}

/** Collect all evidence into a single PHI-masked bundle. */
function collect(): EvidenceBundle {
  const state = store.getState();
  return {
    console: [..._consoleBuffer],
    network: [..._networkBuffer],
    reduxState: maskReduxState(state),
    reduxActions: [..._actionBuffer],
    replayEvents: [..._replayBuffer],
    browserInfo: {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      language: navigator.language,
    },
  };
}

/** Clear all buffers (called after successful submission). */
function clear(): void {
  _consoleBuffer.length = 0;
  _networkBuffer.length = 0;
  _actionBuffer.length = 0;
  _replayBuffer.length = 0;
}

export default { install, uninstall, collect, clear };
